import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API_BASE_URL, Image_URL } from "../config";
import { useCurrency } from "../context/CurrencyContext";

/* ---------- helpers ---------- */

const camelKeys = (row) =>
  Object.fromEntries(
    Object.entries(row).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), v])
  );

const normalizeKt = (kt) => {
  const s = String(kt || "").trim();
  if (!s) return "";
  return /kt$/i.test(s) ? s.toUpperCase() : `${s}KT`;
};

const num = (v) => Number(v) || 0;

const splitImages = (raw) =>
  (raw || "").split(",").map((i) => i.trim()).filter(Boolean);

/* ONE entry per StyleNo + Kt + Color, with every stone line inside */
const groupVariants = (rows) => {
  const map = new Map();

  rows
    .map(camelKeys)
    .sort((a, b) => num(a.id) - num(b.id))
    .forEach((r) => {
      const kt = normalizeKt(r.kt);
      const key = [r.styleNo, kt, r.color].join("_").toLowerCase();
      if (!map.has(key)) map.set(key, { key, kt, lines: [], rows: [] });
      const g = map.get(key);
      g.rows.push(r);
      g.lines.push({
        stoneType: r.stoneType || "",
        shape: r.shape || r.stoneShape || "",
        size: r.size || "",
        colorClarity: r.colorClarity || "",
        setting: r.setting || "",
        settingType: r.settingtype || r.settingType || "",
        carat: num(r.stoneCarat),
        stones: num(r.stoneCount),
      });
    });

  return [...map.values()].map((g) => {
    const base =
      g.rows.find((r) => r.finalPrice !== null && r.finalPrice !== undefined) || g.rows[0];

    const sumCarat = g.lines.reduce((s, l) => s + l.carat, 0);
    const sumStones = g.lines.reduce((s, l) => s + l.stones, 0);
    const uniq = (arr) => [...new Set(arr.filter(Boolean))].join(", ");

    return {
      ...base,
      kt: g.kt,
      variantId: g.key,
      lines: g.lines,
      totalCarat: base.totalCarat ?? sumCarat,
      totalStoneCount: base.totalStoneCount ?? sumStones,
      qtyAvailable: base.qtyAvailable ?? base.quantity ?? g.rows.length ?? 1,
      imagePath: base.imagePath || g.rows.find((r) => r.imagePath)?.imagePath || "",
      stoneShape: uniq(g.lines.map((l) => l.shape)),
      colorClarity: uniq(g.lines.map((l) => l.colorClarity)),
      settingTypes: uniq(g.lines.map((l) => l.settingType)),
    };
  });
};

export default function StockCarryView() {
  const { productcode } = useParams();
  const navigate = useNavigate();
  const { selectedCurrency } = useCurrency();

  const [images, setImages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/InStock/GetAllStockDetails?ProductCode=${encodeURIComponent(
            productcode
          )}&detail=true`
        );
        const json = await res.json();
        const rows = Array.isArray(json) ? json : json?.data;

        if (Array.isArray(rows) && rows.length > 0) {
          const variants = groupVariants(rows);
          setProducts(variants);
          setSelectedIndex(0);
          setImages(splitImages(variants[0].imagePath));
          setActiveIndex(0);
        }
      } catch (err) {
        console.error("Error loading product", err);
      } finally {
        setLoading(false);
      }
    };
    if (productcode) fetchProduct();
  }, [productcode]);

  const nextImage = () => {
    if (!images.length) return;
    setActiveIndex((p) => (p === images.length - 1 ? 0 : p + 1));
  };

  const prevImage = () => {
    if (!images.length) return;
    setActiveIndex((p) => (p === 0 ? images.length - 1 : p - 1));
  };

  const handleSelectVariant = (index) => {
    if (selectedIndex === index) {
      setSelectedIndex(-1); // Toggle off
    } else {
      setSelectedIndex(index); // Active row selected
      setImages(splitImages(products[index].imagePath));
      setActiveIndex(0);
    }
  };

  const getVariantKey = (p) =>
    [p.styleNo || p.productCode, p.kt, p.color, p.category]
      .join("_").toLowerCase().replace(/\s+/g, "");

  const addToCart = (targetIndex) => {
    const product = products[targetIndex];
    if (!product) return;

    const variantKey = getVariantKey(product);
    const activeImg =
      (targetIndex === selectedIndex ? images[activeIndex] : splitImages(product.imagePath)[0]) ||
      product.imagePath || "";
    const price = Number(product.finalPrice || product.unitPriceStyle) || 0;

    const cartItem = {
      id: product.styleNo || product.id,
      variantKey,
      productCode: product.styleNo,
      designNo: product.styleNo,
      category: product.category,
      subCategory: product.subCategory,
      ringSize: product.size || "-",
      stoneShape: product.stoneShape || "",
      colorClarity: product.colorClarity || "",
      settingType: product.settingTypes || "",
      mmSize: product.mmSize || "",
      sieveSize: product.sieveSize || "",
      productCartPrice: price,
      productCheckoutPrice: price,
      quantity: 1,
      maxQty: 1,
      selectedOptions: {
        productimg: activeImg,
        metalKT: product.kt,
        metalColor: product.color,
        metal: `${product.kt} ${product.color}`,
        carat: product.totalCarat,
        size: product.size || "-",
        productCode: product.styleNo,
        shape: product.stoneShape || "",
      },
      selectedValues: {
        MetalWeight: product.metalWeight,
        StoneCount: product.totalStoneCount,
        StoneCarat: product.totalCarat,
      },
      stoneLines: product.lines,
      source: "STOCK",
    };

    const cart = JSON.parse(localStorage.getItem("cartcarry")) || [];
    if (cart.find((i) => i.variantKey === variantKey)) {
      alert("This item is already in the cart");
      return;
    }
    cart.push(cartItem);
    localStorage.setItem("cartcarry", JSON.stringify(cart));
    navigate("/carrycart");
  };

  const getMetalImageUrl = (metalKT, metalColor) => {
    if (!metalKT || !metalColor) return "";
    const kt = metalKT.toLowerCase().replace(/\s+/g, "").replace("kt", "k");
    const color = metalColor.toLowerCase().replace(/\s+/g, "-");
    return `https://westendjewellery.com/assets/img/${kt}-${color}.png`;
  };

  const getFallbackColor = (metalColor) => {
    const col = (metalColor || "").toLowerCase();
    if (col.includes("yellow")) return "#e6ca65";
    if (col.includes("rose")) return "#e8c2b5";
    return "#e8e8e8";
  };

  const formatPrice = (price) => {
    const rate = selectedCurrency?.price || 1;
    const currencyName = selectedCurrency?.name || "AUD";
    const converted = (Number(price || 0) * rate).toFixed(0);
    return `${currencyName} $ ${converted}`;
  };

  const getResolvedImageUrl = (imgStr) => {
    if (!imgStr) return `${Image_URL}/placeholder.png`;
    if (imgStr.startsWith("http://") || imgStr.startsWith("https://")) return imgStr;
    return `${Image_URL}uatproduct/${imgStr}`;
  };

  if (loading) return <p className="text-center mt-5">Loading...</p>;
  if (!products.length) return <p className="text-center mt-5">No product found</p>;

  const selectedProduct = selectedIndex >= 0 ? products[selectedIndex] : null;

  return (
    <div className="home">
      <Header />

      <style>{`
        .sc-table-wrapper {
          border: 1px solid #ebdcc5;
          background: #fff;
        }
        .sc-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }
        .sc-table th {
          background: #fdf8f0;
          color: #222;
          font-weight: 600;
          text-align: left;
          padding: 12px 14px;
          border-bottom: 1px solid #ebdcc5;
          border-right: 1px solid #f2e6d5;
        }
        .sc-table th:last-child {
          border-right: none;
        }
        .sc-table td {
          padding: 12px 14px;
          border-top: 1px solid #f2e6d5;
          border-right: 1px solid #f2e6d5;
          color: #333;
          vertical-align: middle;
          background-color: #ffffff;
        }
        .sc-table td:last-child {
          border-right: none;
        }
        /* ONLY HIGHLIGHT THE ACTIVE SELECTED ROW */
        .sc-table tr.selected td {
          background-color: #faf6ec !important;
        }
        .sc-table tr.clickable {
          cursor: pointer;
        }
        .sc-table .r {
          text-align: right;
        }
        .sc-table .c {
          text-align: center;
        }
        .metal-cell {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .metal-dot {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background-size: cover;
          background-position: center;
          border: 1px solid rgba(0,0,0,0.15);
          display: inline-block;
          flex-shrink: 0;
        }
        .sc-buy-btn {
          background: #222;
          color: #fff;
          border: none;
          padding: 8px 22px;
          font-weight: 600;
          border-radius: 4px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .sc-buy-btn:hover {
          background: #444;
        }
        .sc-detail-heading {
          font-size: 15px;
          color: #444;
          margin-top: 24px;
          margin-bottom: 12px;
        }
      `}</style>

      <div className="container my-4" id="stockCarry_container">
        <div className="row">
          {/* LEFT IMAGE VIEW */}
          <div className="col-md-5">
            <div className="product-image-viewer">
              <div className="main-image-wrapper">
                <button className="nav-arrow left" onClick={prevImage}>‹</button>
                <img
                  src={getResolvedImageUrl(images[activeIndex])}
                  alt="Product"
                  className="main-product-image"
                />
                <button className="nav-arrow right" onClick={nextImage}>›</button>
              </div>

              <div className="thumbnail-row">
                {images.map((img, index) => (
                  <div
                    key={index}
                    className={`thumbnail-box ${index === activeIndex ? "active" : ""}`}
                    onClick={() => setActiveIndex(index)}
                  >
                    <img src={getResolvedImageUrl(img)} alt="thumb" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: EXACT DESIGN WITH SINGLE ROW HIGHLIGHT */}
          <div className="col-md-7">
            <br></br>
            {/* 1. TOP SUMMARY TABLE */}
            <div className="sc-table-wrapper">
              <table className="sc-table">
                <thead>
                  <tr>
                    <th>Metal</th>
                    <th>Product Code</th>
                    <th>Diamond</th>
                    <th>Total Carat</th>
                    <th className="r">Price</th>
                    <th className="c">Qty Available</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, index) => {
                    const imageUrl = getMetalImageUrl(p.kt, p.color);
                    const isSelected = index === selectedIndex;

                    return (
                      <tr
                        key={p.variantId}
                        className={`clickable ${isSelected ? "selected" : ""}`}
                        onClick={() => handleSelectVariant(index)}
                      >
                        <td>
                          <div className="metal-cell">
                            <span
                              className="metal-dot"
                              style={{
                                backgroundColor: getFallbackColor(p.color),
                                backgroundImage: imageUrl ? `url(${imageUrl})` : "none",
                              }}
                            />
                            <span>{p.kt} {p.color}</span>
                          </div>
                        </td>
                        <td>{p.styleNo}</td>
                        <td>{p.colorClarity || "-"}</td>
                        <td>{p.totalCarat ? num(p.totalCarat).toFixed(2) : "-"}</td>
                        <td className="r">{formatPrice(p.finalPrice || p.unitPriceStyle)}</td>
                        <td className="c">{p.qtyAvailable}</td>
                        <td className="r">
                          <button
                            className="sc-buy-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectVariant(index);
                              addToCart(index);
                            }}
                          >
                            Buy
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 2. STONE DETAILS TABLE FOR SELECTED VARIANT */}
            {/* {selectedProduct && (
              <div>
                <div className="sc-detail-heading">
                  Stone Details for: <b>{selectedProduct.kt} {selectedProduct.color} ({selectedProduct.styleNo})</b>
                </div>
                <div className="sc-table-wrapper">
                  <table className="sc-table">
                    <thead>
                      <tr>
                        <th>Shape</th>
                        <th>Size</th>
                        <th>Color / Clarity</th>
                        <th className="r">Carat</th>
                        <th className="r">Stones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProduct.lines.map((l, i) => (
                        <tr key={i}>
                          <td>{l.shape || "-"}</td>
                          <td>{l.size ? `${l.size} mm` : "-"}</td>
                          <td>{l.colorClarity || "-"}</td>
                          <td className="r">{l.carat.toFixed(2)}</td>
                          <td className="r">{l.stones}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} style={{ fontWeight: 700, backgroundColor: "#faf6ec" }}>Total</td>
                        <td className="r" style={{ fontWeight: 700, backgroundColor: "#faf6ec" }}>
                          {num(selectedProduct.totalCarat).toFixed(2)}
                        </td>
                        <td className="r" style={{ fontWeight: 700, backgroundColor: "#faf6ec" }}>
                          {num(selectedProduct.totalStoneCount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )} */}
          </div>
        </div>
      </div>
      <br />
      <Footer />
    </div>
  );
}